import { DISTRICTS } from "./districts";
import { RACES } from "./races";
import { shortName } from "./survey";
import type {
  Candidate,
  ExternalRow,
  Office,
  Party,
  ResultCount,
  ResultsCandidate,
  ResultsRace,
  Side,
} from "./types";

/* ── Live results ──────────────────────────────────
 * Turns civicAPI's counts and calls into per-race rows. Each row keeps the
 * exact count and reads it like Foresight does: the margin is left minus
 * right in points of the two-party vote, and a called race carries certain
 * odds. Sides are found by the candidate names races.ts and districts.ts
 * list, then by party. The national House vote sets the generic ballot.
 */

/* ── Constants ─────────────────────────────────── */

const DISTRICTS_BY_ID = new Map(DISTRICTS.map((d) => [d.id, d]));

const OFFICE_BY_TYPE: Partial<Record<string, Office>> = {
  Governor: "governor",
  "House of Representatives": "house",
  "US Senate": "senate",
};

const PARTY_BY_NAME: Partial<Record<string, Party>> = {
  Democratic: "D",
  Republican: "R",
};

/* ── Helpers ───────────────────────────────────── */

const partyOf = (candidate: ResultsCandidate): Party =>
  PARTY_BY_NAME[candidate.party] ?? "I";

const toCount = (candidate: ResultsCandidate): ResultCount => ({
  listed: candidate.party,
  name: candidate.name.replace(/\s+/g, " ").trim(),
  party: partyOf(candidate),
  votes: candidate.votes,
});

/** Lowercase words without accents or punctuation ("Luján" → "lujan"). */
const words = (name: string): string[] =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z']+/)
    .filter(Boolean);

/** True when the listed name contains the candidate's short name. */
const isNamed = (name: string, candidate: Candidate): boolean => {
  const target = words(shortName(candidate));
  const tokens = words(name);
  return tokens.some((_, i) => target.every((w, j) => tokens[i + j] === w));
};

/** The feed's line for one side: the candidate named (same party first,
    then most votes), else the top vote-getter of the side's major party. */
const findSide = (
  candidates: ResultsCandidate[],
  side: Candidate | null,
  fallback: Party
): ResultsCandidate | null => {
  const ranked = [...candidates].sort((a, b) => b.votes - a.votes);
  if (side) {
    const named = ranked.filter((c) => isNamed(c.name, side));
    const match = named.find((c) => partyOf(c) === side.party) ?? named[0];
    if (match) return match;
  }
  const party = side?.party ?? fallback;
  if (party === "I") return null;
  return ranked.find((c) => partyOf(c) === party) ?? null;
};

/** The count between the two sides; null before any vote counts or call.
    Uncontested seats some states don't count are called with no margin. */
const readRace = (
  race: ResultsRace,
  left: Candidate | null,
  right: Candidate | null
): Pick<ExternalRow, "leftProbability" | "margin" | "result"> | null => {
  const l = findSide(race.candidates, left, "D");
  const r = findSide(race.candidates, right, "R");
  if ((!l && !r) || l === r) return null;
  const winner = race.candidates.find((c) => c.winner);
  const called: Side | null = !winner
    ? null
    : winner === l
      ? "left"
      : winner === r
        ? "right"
        : null;
  const leftVotes = l?.votes ?? 0;
  const rightVotes = r?.votes ?? 0;
  const margin =
    leftVotes + rightVotes > 0
      ? ((leftVotes - rightVotes) / (leftVotes + rightVotes)) * 100
      : null;
  if (called === null && margin === null) return null;
  return {
    leftProbability: called === null ? null : called === "left" ? 1 : 0,
    margin,
    result: {
      called,
      counts: [...race.candidates]
        .sort((a, b) => b.votes - a.votes)
        .map(toCount),
      left: l ? toCount(l) : null,
      reporting: race.percent_reporting,
      right: r ? toCount(r) : null,
      totalVotes: race.candidates.reduce((sum, c) => sum + c.votes, 0),
    },
  };
};

/* ── Parser ────────────────────────────────────── */

/** Senate, governor and House district rows plus the national House vote;
    races the tracker doesn't list (territories, national totals of the
    other offices) are skipped. */
export const parseResults = (races: ResultsRace[]): ExternalRow[] =>
  races.flatMap((race): ExternalRow[] => {
    const office = OFFICE_BY_TYPE[race.type];
    if (!office) return [];

    if (office === "house" && race.province === "US") {
      const read = readRace(race, null, null);
      return read ? [{ ...read, office, state: "US" }] : [];
    }

    if (office === "house") {
      const district = race.district
        ? DISTRICTS_BY_ID.get(race.district)
        : undefined;
      if (!district) return [];
      const read = readRace(race, district.left, district.right);
      return read
        ? [{ ...read, district: district.id, office, state: district.state }]
        : [];
    }

    const listed = RACES.find(
      (r) => r.office === office && r.state === race.province
    );
    if (!listed) return [];
    const read = readRace(race, listed.left, listed.right);
    return read ? [{ ...read, office, state: listed.state }] : [];
  });
